import React from 'react';
import { CouncilorDTO } from '../../services/councilorService';
import { SecretariaMunicipalDTO } from '../../services/secretariaMunicipalService';
import { TipoEmendaDTO } from '../../services/tipoEmendaService';
import { UI_THEME } from '../../ui/theme';

export type Filters = {
  exercicio: string;
  status: string;
  secretariaDestino: string;
  classificacao: string;
  esfera: string;
  tipoTransferencia: string;
  councilorId: string;
  dataInicio: string;
  dataFim: string;
  sort: string;
};

export const EMPTY_FILTERS: Filters = {
  exercicio: '',
  status: '',
  secretariaDestino: '',
  classificacao: '',
  esfera: '',
  tipoTransferencia: '',
  councilorId: '',
  dataInicio: '',
  dataFim: '',
  sort: 'data,desc',
};

export const STATUS_OPTIONS = [
  'Em aprovação',
  'Recebido',
  'Em análise de admissibilidade',
  'Admissibilidade aprovada',
  'Devolvida ao legislativo',
  'Em análise de demanda',
  'Análise de demanda aprovada',
  'Devolvida por incompatibilidade de demanda',
  'Em análise documental',
  'Análise documental aprovada',
  'Devolvida por inviabilidade documental',
  'Iniciado',
  'Em execução',
  'Concluído',
  'Devolvido',
];

export const ESFERA_OPTIONS = ['Municipal', 'Estadual', 'Federal'];

export const sortOptions = [
  { value: 'data,desc', label: 'Data mais recente' },
  { value: 'data,asc', label: 'Data mais antiga' },
  { value: 'codigoOficial,asc', label: 'Código oficial A-Z' },
  { value: 'valor,desc', label: 'Maior valor' },
  { value: 'valor,asc', label: 'Menor valor' },
  { value: 'parlamentar,asc', label: 'Parlamentar A-Z' },
  { value: 'secretariaDestino,asc', label: 'Secretaria A-Z' },
  { value: 'statusCicloVida,asc', label: 'Etapa A-Z' },
];

interface ReportEmendasFiltersProps {
  filters: Filters;
  onChange: (f: Filters) => void;
  activeTab: string | null;
  tipos: TipoEmendaDTO[];
  councilors: CouncilorDTO[];
  secretarias: SecretariaMunicipalDTO[];
  loading: boolean;
  hasSearched: boolean;
  exporting: 'xlsx' | 'pdf' | null;
  onVisualizar: () => void;
  onExportXLSX: () => void;
  onExportPDF: () => void;
  onClear: () => void;
}

const ReportEmendasFilters: React.FC<ReportEmendasFiltersProps> = ({
  filters,
  onChange,
  activeTab,
  tipos,
  councilors,
  secretarias,
  loading,
  hasSearched,
  exporting,
  onVisualizar,
  onExportXLSX,
  onExportPDF,
  onClear,
}) => {
  const isSaas = UI_THEME === 'saas';
  const inputClass = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40 print:border-slate-300 print:bg-white print:text-slate-700';
  const labelClass = 'space-y-1 text-sm text-slate-600 dark:text-slate-300 print:text-slate-600';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:opacity-60 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500';
  const actionButtonClass =
    'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const darkButtonClass =
    'inline-flex items-center justify-center rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-600 dark:hover:bg-slate-500';

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className={labelClass}>
          <span className="font-medium">Exercício</span>
          <input
            type="number"
            value={filters.exercicio}
            onChange={e => onChange({ ...filters, exercicio: e.target.value })}
            placeholder="Ex: 2026"
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Etapa</span>
          <select
            value={filters.status}
            onChange={e => onChange({ ...filters, status: e.target.value })}
            className={inputClass}
          >
            <option value="">Todos</option>
            {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Secretaria</span>
          <select
            value={filters.secretariaDestino}
            onChange={e => onChange({ ...filters, secretariaDestino: e.target.value })}
            className={inputClass}
          >
            <option value="">Todas</option>
            {secretarias.map(s => <option key={s.id} value={s.nome}>{s.nome}</option>)}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Tipo</span>
          <select
            value={filters.classificacao}
            onChange={e => onChange({ ...filters, classificacao: e.target.value })}
            className={inputClass}
          >
            <option value="">Todos</option>
            {tipos.map(t => <option key={t.codigo} value={t.codigo}>{t.nome}</option>)}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Esfera</span>
          <select
            value={filters.esfera}
            onChange={e => onChange({ ...filters, esfera: e.target.value })}
            className={inputClass}
          >
            <option value="">Todas</option>
            {ESFERA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Parlamentar</span>
          <select
            value={filters.councilorId}
            onChange={e => onChange({ ...filters, councilorId: e.target.value })}
            className={inputClass}
          >
            <option value="">Todos</option>
            {councilors.map(c => <option key={c.councilorId} value={c.councilorId}>{c.fullName}</option>)}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Execução</span>
          <select
            value={filters.tipoTransferencia}
            onChange={e => onChange({ ...filters, tipoTransferencia: e.target.value })}
            className={inputClass}
          >
            <option value="">Todas</option>
            <option value="Direta">Direta</option>
            <option value="Indireta">Indireta</option>
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data inicial</span>
          <input
            type="date"
            value={filters.dataInicio}
            onChange={e => onChange({ ...filters, dataInicio: e.target.value })}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data final</span>
          <input
            type="date"
            value={filters.dataFim}
            onChange={e => onChange({ ...filters, dataFim: e.target.value })}
            className={inputClass}
          />
        </label>
        {activeTab === 'relacao' && (
          <label className={`${labelClass} xl:col-span-4`}>
            <span className="font-medium">Ordenação</span>
            <select
              value={filters.sort}
              onChange={e => onChange({ ...filters, sort: e.target.value })}
              className={inputClass}
            >
              {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={onVisualizar}
          disabled={loading}
          className={primaryButtonClass}
        >
          {loading ? 'Carregando...' : 'Visualizar'}
        </button>
        {hasSearched && activeTab === 'relacao' && (
          <>
            <button
              type="button"
              onClick={onExportXLSX}
              disabled={exporting !== null}
              className={actionButtonClass}
            >
              {exporting === 'xlsx' ? 'Exportando...' : 'Exportar XLSX'}
            </button>
            <button
              type="button"
              onClick={onExportPDF}
              disabled={exporting !== null}
              className={darkButtonClass}
            >
              {exporting === 'pdf' ? 'Exportando...' : 'Exportar PDF'}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClear}
          className={actionButtonClass}
        >
          Limpar filtros
        </button>
      </div>
    </>
  );
};

export default ReportEmendasFilters;
